import { useMutation, useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { Button, Label, TextInput, useView, Card, Animate } from 'components/lib'
import React, { useEffect, useState } from 'react'
import Nav from './Nav'

export default function ShortCodeLanding() {
    const view = useView()
    const [logo, setLogo] = useState(null)
    const [settings, setSettings] = useState({
        logo: null,
        text: '',
        description: '',
        background: '#14263a',
        color: '#ffffff'
    })

    const { data, isSuccess, isLoading, refetch } = useQuery({
        queryKey: ['settings', 'shortcode_landing'],
        queryFn: () => axios.get('/api/settings/shortcode_landing').then(res => res.data)
    })

    useEffect(() => {
        if (data && isSuccess) {
            setSettings({
                logo: data.data?.settings?.logo || null,
                text: data.data?.settings?.text || '',
                description: data.data?.settings?.description || '',
                background: data.data?.settings?.background || '#14263a',
                color: data.data?.settings?.color || '#ffffff'
            })
        }
    }, [data, isSuccess])

    const saveM = useMutation({
        mutationKey: ['settings', 'shortcode_landing', 'save'],
        mutationFn: (formData) => axios.put('/api/settings/shortcode_landing', formData).then(res => res.data),
    })

    useEffect(() => {
        if (saveM.isError) {
            view.notification.show(saveM.error?.response?.data?.message || saveM.error.message, 'error')
        }
    }, [saveM.isError])

    useEffect(() => {
        if (saveM.isSuccess) {
            view.notification.show(saveM.data?.data?.message, 'success', true)
            setLogo(null)
            refetch()
        }
    }, [saveM.isSuccess])

    const handleSave = () => {
        const formData = new FormData()
        if (logo) {
            formData.append('logo', logo)
        }
        formData.append('text', settings.text)
        formData.append('description', settings.description)
        formData.append('background', settings.background)
        formData.append('color', settings.color)

        saveM.mutate(formData)
    }

    return (
        <>
            <Nav />
            <Animate>
                <Card loading={isLoading} title="Short Code Landing Page Defaults">
                    <p className="text-sm text-gray-500 mb-6">
                        Configure global default settings for the short code landing page.
                        These values will be used when campaigns don't have custom settings.
                    </p>

                    <form encType="multipart/form-data" className="grid gap-4">
                        <div>
                            <Label text="Logo" />
                            <input
                                type="file"
                                accept="image/*"
                                onChange={e => setLogo(e.target.files[0])}
                            />
                            {(logo || settings?.logo) && (
                                <div className="bg-gray-200 mt-2 p-2 rounded">
                                    <img
                                        src={logo ? URL.createObjectURL(logo) : settings?.logo || ''}
                                        className="mx-auto w-full h-full max-w-[350px] object-cover rounded"
                                        alt="logo preview"
                                    />
                                </div>
                            )}
                        </div>

                        <TextInput
                            label="Text (Headline)"
                            name="text"
                            value={settings?.text}
                            onChange={(name, value) => setSettings({ ...settings, [name]: value })}
                        />

                        <TextInput
                            label="Description"
                            type="textarea"
                            name="description"
                            value={settings?.description}
                            onChange={(name, value) => setSettings({ ...settings, [name]: value })}
                        />

                        <div>
                            <Label text="Background Color" />
                            <input
                                type="color"
                                value={settings?.background}
                                onChange={e => setSettings({ ...settings, background: e.target.value })}
                                className="w-16 h-10 cursor-pointer"
                            />
                            <span className="ml-2 text-sm text-gray-600">{settings?.background}</span>
                        </div>

                        <div>
                            <Label text="Text Color" />
                            <input
                                type="color"
                                value={settings?.color}
                                onChange={e => setSettings({ ...settings, color: e.target.value })}
                                className="w-16 h-10 cursor-pointer"
                            />
                            <span className="ml-2 text-sm text-gray-600">{settings?.color}</span>
                        </div>

                        <Button
                            text="Save"
                            small
                            loading={saveM.isPending}
                            action={handleSave}
                        />
                    </form>
                </Card>
            </Animate>
        </>
    )
}
